import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import type { ARCommunication } from '../../data/mockARData';

interface PhoneCallDetailModalProps {
  communication: ARCommunication | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function PhoneCallDetailModal({
  communication,
  open,
  onOpenChange,
}: PhoneCallDetailModalProps) {
  if (!communication) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Phone Call Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Meta info */}
          <div className="space-y-2">
            <div className="flex gap-8">
              <span className="text-muted-foreground w-28">Date</span>
              <span>{formatDateTime(communication.date)}</span>
            </div>
            <div className="flex gap-8">
              <span className="text-muted-foreground w-28">Contact Person</span>
              <span>{communication.contactPerson}</span>
            </div>
            <div className="flex gap-8">
              <span className="text-muted-foreground w-28">Response</span>
              <span>{communication.response}</span>
            </div>
          </div>

          {/* Call Recording */}
          <div className="space-y-2">
            <h3 className="font-semibold">Call Transcript</h3>
            <div className="text-sm text-muted-foreground">Call Recording</div>
            <div className="border rounded-lg p-3 bg-muted/30">
              <audio controls className="w-full">
                <source src={communication.audioUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
              <div className="text-center text-sm text-muted-foreground mt-1">
                0:00 / {communication.audioDuration || '0:00'}
              </div>
            </div>
          </div>

          {/* Call Transcript */}
          {communication.transcript && communication.transcript.length > 0 && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30 max-h-[300px] overflow-y-auto">
              {communication.transcript.map((line, index) => (
                <div key={index} className="space-y-1">
                  <div
                    className={cn(
                      'text-xs font-semibold',
                      line.speaker === 'Customer'
                        ? 'text-blue-600'
                        : 'text-green-600',
                    )}
                  >
                    {line.speaker}
                  </div>
                  <div className="text-sm">{line.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
